import type { ReactNode } from 'react';
import { Box, Card, CardContent, Stack, Typography } from '@mui/material';
import AnnouncementBell from '@/components/common/AnnouncementBell';

interface PageShellProps {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  action?: ReactNode;
  showAnnouncements?: boolean;
  reserveBottomNav?: boolean;
  children: ReactNode;
}

interface SectionHeaderProps {
  title: string;
  detail?: ReactNode;
  action?: ReactNode;
}

interface MetricCardProps {
  label: string;
  value: ReactNode;
  helper?: ReactNode;
  icon?: ReactNode;
  tone?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
}

export function PageShell({
  eyebrow,
  title,
  subtitle,
  action,
  showAnnouncements = false,
  reserveBottomNav = true,
  children,
}: PageShellProps) {
  const bottomPadding = reserveBottomNav
    ? 'calc(var(--app-bottom-obstruction) + var(--app-page-bottom-padding))'
    : 'calc(var(--app-safe-area-bottom) + var(--app-page-bottom-padding))';

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100%',
        pl: {
          xs: 'calc(var(--app-page-inline-padding) + var(--app-safe-area-left))',
          sm: 'calc(var(--app-page-inline-padding) + var(--app-safe-area-left))',
        },
        pr: {
          xs: 'calc(var(--app-page-inline-padding) + var(--app-safe-area-right))',
          sm: 'calc(var(--app-page-inline-padding) + var(--app-safe-area-right))',
        },
        pt: {
          xs: 'var(--app-page-top-padding)',
          sm: 'var(--app-page-top-padding)',
        },
        pb: {
          xs: bottomPadding,
          sm: bottomPadding,
        },
      }}
    >
      {showAnnouncements && (
        <Box
          sx={{
            position: 'absolute',
            display: { xs: 'block', sm: 'none' },
            top: {
              xs: 'calc(var(--app-page-top-padding) + 6px)',
              sm: 'calc(var(--app-page-top-padding) + 13px)',
            },
            right: 'calc(var(--app-page-inline-padding) + var(--app-safe-area-right))',
            zIndex: 2,
          }}
        >
          <AnnouncementBell />
        </Box>
      )}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'flex-end' }}
        spacing={2}
        sx={{ mb: 2.5 }}
      >
        <Box sx={{ minWidth: 0 }}>
          {eyebrow && (
            <Typography
              variant="overline"
              color="primary.main"
              sx={{ display: 'block', fontWeight: 800, lineHeight: 1.4 }}
            >
              {eyebrow}
            </Typography>
          )}
          <Typography variant="h4" sx={{ fontSize: { xs: '2rem', sm: '2.4rem' } }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography color="text.secondary" sx={{ mt: 0.75, maxWidth: 620 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {(action || showAnnouncements) && (
          <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1} sx={{ flexShrink: 0 }}>
            {action && <Box sx={{ minWidth: 0 }}>{action}</Box>}
            {showAnnouncements && (
              <Box sx={{ display: { xs: 'none', sm: 'block' }, flexShrink: 0 }}>
                <AnnouncementBell />
              </Box>
            )}
          </Stack>
        )}
      </Stack>
      {children}
    </Box>
  );
}

export function SectionHeader({ title, detail, action }: SectionHeaderProps) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={2}
      sx={{ mt: 2.5, mb: 1.25 }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h6">{title}</Typography>
        {detail && (
          <Typography variant="body2" color="text.secondary">
            {detail}
          </Typography>
        )}
      </Box>
      {action}
    </Stack>
  );
}

export function MetricCard({ label, value, helper, icon, tone = 'primary' }: MetricCardProps) {
  return (
    <Card sx={{ position: 'relative', height: '100%', overflow: 'hidden' }}>
      {icon && (
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            left: { xs: 12, sm: 16 },
            bottom: { xs: -30, sm: -38 },
            width: { xs: 96, sm: 112 },
            height: { xs: 96, sm: 112 },
            display: 'grid',
            placeItems: 'center',
            fontSize: { xs: 82, sm: 96 },
            color: `${tone}.main`,
            opacity: 0.16,
            filter: 'grayscale(1) saturate(0.55) contrast(1.08)',
            pointerEvents: 'none',
            '& .MuiSvgIcon-root': {
              fontSize: 'inherit',
            },
          }}
        >
          {icon}
        </Box>
      )}
      <CardContent sx={{ position: 'relative', zIndex: 1, p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
            {label}
          </Typography>
          <Typography variant="h5" sx={{ mt: 0.5 }}>
            {value}
          </Typography>
        </Box>
        {helper && (
          <Typography variant="body2" color="text.secondary" sx={{ position: 'relative', zIndex: 1, mt: 1 }}>
            {helper}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
